/**
 * Allocation Tracking Zustand Store
 * Based on docs/spec/allocation/07-integration.md Section 8.2
 *
 * Manages client-side state for allocation features.
 * Data fetching is handled by React Query hooks.
 */

import { create } from 'zustand';
import type {
  PortfolioAllocation,
  BucketAllocationSummary,
  AccountWithTarget,
  AllocationWarning,
  AllocationTemplate,
  AssetClass,
} from '../types/allocation';

// ============================================================================
// State Types
// ============================================================================

interface AllocationFilters {
  /** Filter holdings by asset class */
  assetClassFilter: AssetClass | null;
  /** Filter holdings needing review */
  needsReviewOnly: boolean;
  /** Filter warnings by status */
  warningStatusFilter: 'active' | 'dismissed' | 'all';
}

interface AllocationUIState {
  /** Currently selected tab in allocation view */
  activeTab: 'portfolio' | 'buckets' | 'targets' | 'holdings' | 'warnings';
  /** Account being edited for target assignment */
  editingAccountId: string | null;
  /** Account being assigned to buckets */
  assigningAccountId: string | null;
  /** Holding being classified */
  classifyingHoldingId: string | null;
  /** Template being previewed */
  previewingTemplateId: string | null;
  /** Show template selection modal */
  showTemplateModal: boolean;
  /** Show bulk classify modal */
  showBulkClassifyModal: boolean;
  /** Show configuration modal (WP-ALLOC-CONFIG-1) */
  showConfigModal: boolean;
  /** Selected holdings for bulk operations */
  selectedHoldingIds: string[];
}

interface AllocationState {
  // Cached data (synced with React Query)
  portfolio: PortfolioAllocation | null;
  buckets: BucketAllocationSummary | null;
  accountTargets: AccountWithTarget[];
  warnings: AllocationWarning[];
  templates: AllocationTemplate[];

  // UI State
  ui: AllocationUIState;
  filters: AllocationFilters;

  // Actions
  setPortfolio: (portfolio: PortfolioAllocation | null) => void;
  setBuckets: (buckets: BucketAllocationSummary | null) => void;
  setAccountTargets: (targets: AccountWithTarget[]) => void;
  setWarnings: (warnings: AllocationWarning[]) => void;
  setTemplates: (templates: AllocationTemplate[]) => void;

  // UI Actions
  setActiveTab: (tab: AllocationUIState['activeTab']) => void;
  setEditingAccountId: (id: string | null) => void;
  setAssigningAccountId: (id: string | null) => void;
  setClassifyingHoldingId: (id: string | null) => void;
  setPreviewingTemplateId: (id: string | null) => void;
  setShowTemplateModal: (show: boolean) => void;
  setShowBulkClassifyModal: (show: boolean) => void;
  setShowConfigModal: (show: boolean) => void;
  toggleHoldingSelection: (id: string) => void;
  selectAllHoldings: (ids: string[]) => void;
  clearHoldingSelection: () => void;

  // Filter Actions
  setAssetClassFilter: (filter: AssetClass | null) => void;
  setNeedsReviewOnly: (value: boolean) => void;
  setWarningStatusFilter: (filter: 'active' | 'dismissed' | 'all') => void;
  resetFilters: () => void;

  // Computed values
  getActiveWarningsCount: () => number;
  getHoldingsNeedingReview: () => number;
  getDriftSummary: () => {
    overAllocated: AssetClass[];
    underAllocated: AssetClass[];
  };

  // Reset
  reset: () => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialUIState: AllocationUIState = {
  activeTab: 'portfolio',
  editingAccountId: null,
  assigningAccountId: null,
  classifyingHoldingId: null,
  previewingTemplateId: null,
  showTemplateModal: false,
  showBulkClassifyModal: false,
  showConfigModal: false,
  selectedHoldingIds: [],
};

const initialFilters: AllocationFilters = {
  assetClassFilter: null,
  needsReviewOnly: false,
  warningStatusFilter: 'active',
};

// ============================================================================
// Store
// ============================================================================

export const useAllocationStore = create<AllocationState>((set, get) => ({
  // Initial data state
  portfolio: null,
  buckets: null,
  accountTargets: [],
  warnings: [],
  templates: [],

  // Initial UI state
  ui: initialUIState,
  filters: initialFilters,

  // Data setters (called by React Query on successful fetches)
  setPortfolio: (portfolio) => set({ portfolio }),
  setBuckets: (buckets) => set({ buckets }),
  setAccountTargets: (targets) => set({ accountTargets: targets }),
  setWarnings: (warnings) => set({ warnings }),
  setTemplates: (templates) => set({ templates }),

  // UI Actions
  setActiveTab: (tab) =>
    set((state) => ({ ui: { ...state.ui, activeTab: tab } })),

  setEditingAccountId: (id) =>
    set((state) => ({ ui: { ...state.ui, editingAccountId: id } })),

  setAssigningAccountId: (id) =>
    set((state) => ({ ui: { ...state.ui, assigningAccountId: id } })),

  setClassifyingHoldingId: (id) =>
    set((state) => ({ ui: { ...state.ui, classifyingHoldingId: id } })),

  setPreviewingTemplateId: (id) =>
    set((state) => ({ ui: { ...state.ui, previewingTemplateId: id } })),

  setShowTemplateModal: (show) =>
    set((state) => ({ ui: { ...state.ui, showTemplateModal: show } })),

  setShowBulkClassifyModal: (show) =>
    set((state) => ({ ui: { ...state.ui, showBulkClassifyModal: show } })),

  setShowConfigModal: (show) =>
    set((state) => ({ ui: { ...state.ui, showConfigModal: show } })),

  toggleHoldingSelection: (id) =>
    set((state) => {
      const current = state.ui.selectedHoldingIds;
      const updated = current.includes(id)
        ? current.filter((hid) => hid !== id)
        : [...current, id];
      return { ui: { ...state.ui, selectedHoldingIds: updated } };
    }),

  selectAllHoldings: (ids) =>
    set((state) => ({ ui: { ...state.ui, selectedHoldingIds: ids } })),

  clearHoldingSelection: () =>
    set((state) => ({ ui: { ...state.ui, selectedHoldingIds: [] } })),

  // Filter Actions
  setAssetClassFilter: (filter) =>
    set((state) => ({ filters: { ...state.filters, assetClassFilter: filter } })),

  setNeedsReviewOnly: (value) =>
    set((state) => ({ filters: { ...state.filters, needsReviewOnly: value } })),

  setWarningStatusFilter: (filter) =>
    set((state) => ({ filters: { ...state.filters, warningStatusFilter: filter } })),

  resetFilters: () => set({ filters: initialFilters }),

  // Computed values
  getActiveWarningsCount: () => {
    return get().warnings.filter((w) => w.status === 'active').length;
  },

  getHoldingsNeedingReview: () => {
    return get().portfolio?.unclassifiedHoldings ?? 0;
  },

  getDriftSummary: () => {
    const portfolio = get().portfolio;
    if (!portfolio) {
      return { overAllocated: [], underAllocated: [] };
    }

    const overAllocated: AssetClass[] = [];
    const underAllocated: AssetClass[] = [];

    for (const allocation of portfolio.allocations) {
      if (allocation.driftDirection === 'over') {
        overAllocated.push(allocation.assetClass);
      } else if (allocation.driftDirection === 'under') {
        underAllocated.push(allocation.assetClass);
      }
    }

    return { overAllocated, underAllocated };
  },

  // Reset
  reset: () =>
    set({
      portfolio: null,
      buckets: null,
      accountTargets: [],
      warnings: [],
      templates: [],
      ui: initialUIState,
      filters: initialFilters,
    }),
}));

// ============================================================================
// Selectors (for use with shallow comparison)
// ============================================================================

export const selectPortfolio = (state: AllocationState) => state.portfolio;
export const selectBuckets = (state: AllocationState) => state.buckets;
export const selectAccountTargets = (state: AllocationState) => state.accountTargets;
export const selectWarnings = (state: AllocationState) => state.warnings;
export const selectTemplates = (state: AllocationState) => state.templates;
export const selectActiveTab = (state: AllocationState) => state.ui.activeTab;
export const selectFilters = (state: AllocationState) => state.filters;
export const selectSelectedHoldingIds = (state: AllocationState) =>
  state.ui.selectedHoldingIds;

export default useAllocationStore;
